import { useCallback } from 'react';
import { TableWithHeader } from './shared/TableWithHeader';
import { DataTableRow } from '../../common/table/TableTypes';
import {
  DocumentationCardContentViewType,
  DocumentationCardItem,
} from '../salesforce-documentation-card/types';
import { TableTypography } from '../../common/table/utils';
import { TableItemHighlightedText } from '../building-blocks/documentation-card/search/highlight/TableItemHighlightedText';
import { PossibleSectionInDependencies } from '../building-blocks/documentation-card/search/types';
import { NameProperties } from '../../parser/ParserTypes';
import { OBJECT } from '@server/parser';

const columns = [
  { field: 'label', headerName: 'Label', width: '200px' },
  { field: 'name', headerName: 'API Name' },
];

const renderRows = ({
  items,
  section = 'dependencies',
  dataTableType = OBJECT,
}: {
  items: NameProperties[];
  section?: PossibleSectionInDependencies;
  dataTableType?: string;
}) => {
  return items.map((row, index) => ({
    id: row.name,
    label: (
      <TableTypography>
        <TableItemHighlightedText
          text={row.label}
          field="label"
          section={section}
          tableType={dataTableType}
          rowIndex={index}
        />
      </TableTypography>
    ),
    name: (
      <TableTypography>
        <TableItemHighlightedText
          text={row.name}
          field="name"
          section={section}
          tableType={dataTableType}
          rowIndex={index}
        />
      </TableTypography>
    ),
    _nameStr: row.name, //needed in onRowClick
    isClickable: true,
    itemData: row,
  }));
};

interface ObjectTableProps {
  items: NameProperties[];
  onItemClick: (props: DocumentationCardItem) => void;
  clearHistory?: boolean;
  section?: PossibleSectionInDependencies;
  dataTableType?: string;
}

export const ObjectsTable = ({
  items,
  onItemClick,
  clearHistory,
  section = 'dependencies',
  dataTableType = OBJECT,
}: ObjectTableProps) => {
  const onRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>, row: DataTableRow) => {
      onItemClick({
        item: { ...row.itemData, parentType: OBJECT },
        clearHistory,
        contentType: DocumentationCardContentViewType.default,
      });
    },
    [onItemClick, clearHistory],
  );

  return (
    <TableWithHeader
      tableHeader="Objects"
      columns={columns}
      rows={renderRows({ items, section, dataTableType })}
      onRowClick={onRowClick}
      dataTableType={dataTableType}
      section={section}
    />
  );
};
